import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import { ExpressionReferenceInput } from '@framework/engine/schema/expressions/references/input/ExpressionReferenceInput.tsx';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { WidgetConfigurationSlotRender } from '@framework/engine/schema/widgets/repository/common/config-slots/WidgetConfigurationSlotRender.tsx';
import { CommonConfigComponent } from '@framework/engine/schema/widgets/repository/inputs/common/CommonInputConfigComponent.tsx';
import { useTreeReferencesContext } from '@framework/engine/tree/references/TreeReferencesContext.tsx';
import { useRelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { CheckboxGroupInput } from '@framework/form/inputs/CheckboxGroupInput.tsx';
import { CheckboxInput } from '@framework/form/inputs/CheckboxInput.tsx';
import { DecimalInput } from '@framework/form/inputs/DecimalInput.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { RowElement } from '@framework/ui/components/row/RowElement.tsx';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { MunicipalityGroupListSelectInput } from '../province/MunicipalityGroupListSelectInput.tsx';
import { MapCompetencesConfigSection } from './config/MapCompetencesConfigSection.tsx';
import {
  MapCompetencesSlot,
  MapGroupsAndLayersSlot,
  MapIdentifySlot,
  MapIntersectionsSlot,
  MapLabelingSlot,
  MapMunicipalityGroupSlot,
  MapPositionSlot,
  MapPrintImagesSlot,
  MapSelectionSlot,
} from './config/MapConfigSlots.tsx';
import { MapExtentInput } from './config/MapExtentInput.tsx';
import { MapIdentifyConfigSection } from './config/MapIdentifyConfigSection.tsx';
import { MapIntersectionsConfigSection } from './config/MapIntersectionsConfigSection.tsx';
import { MapPrintImagesConfigSection } from './config/print/MapPrintImagesConfigSection.tsx';
import { MapGroupsAndLayersTreeSection } from './config/tree/MapGroupsAndLayersTreeSection.tsx';
import { type IMapWidget, MapWidgetSelectionShape } from './IMapWidget.ts';
import { getRenderableIntersectionLayers } from './layers/getIntersectionLayersInfo.ts';
import { type IMapGroup } from './layers/MapGroup.ts';
import { type IMapLayer } from './layers/MapLayer.ts';
import { MapWidgetLogic } from './MapWidgetLogic.ts';

export interface IMapWidgetConfigComponentProps
  extends IWidgetComponentProps<IMapWidget> {}

export function MapWidgetConfigComponent(
  props: IMapWidgetConfigComponentProps,
) {
  // Un solo editor per campo: gruppi e livelli condividono gli editor per evitare doppi useFieldArray.
  const groupEditor = useRelationEditor<IMapGroup>('groups');
  const layersEditor = useRelationEditor<IMapLayer>('layers');

  // Il default della mappa è impostabile solo come reference a un tag semantico
  // di tipo MAPPA: le reference dei widget mappa hanno `Datatype.Array` (solo
  // le geometrie), quindi il filtro su `Datatype.Map` le esclude già.
  const context = useTreeReferencesContext();
  const defaultValueReferences = useMemo(() => {
    return context.references.filter(
      reference => reference.field.type === Datatype.Map,
    );
  }, [context.references]);

  // Il buffer serve solo per bufferizzare punti/linee prima dell'intersezione:
  // mostriamo l'input solo quando tali geometrie sono selezionabili.
  const shapes: MapWidgetSelectionShape[] =
    useWatch({ name: 'selection.shapes' }) ?? [];
  const showBufferDistance =
    shapes.includes(MapWidgetSelectionShape.Point) ||
    shapes.includes(MapWidgetSelectionShape.Line);

  // Layer con intersezione visibile, filtrati per feature flag: con le
  // funzionalità avanzate disattivate restano solo i preset catastali.
  const intersectionLayers = useMemo(
    () => getRenderableIntersectionLayers(layersEditor.items),
    [layersEditor.items],
  );

  // Con le funzionalità avanzate mostriamo sempre il pannello (superficie
  // di configurazione delle intersezioni); in modalità solo catastale lo
  // nascondiamo se non ci sono intersezioni catastali da elencare.
  const showIntersectionsPanel =
    MapWidgetLogic.isAdvancedMapWidgetEnabled() ||
    (MapWidgetLogic.isCadastralEnabled() && intersectionLayers.length > 0);

  return (
    <>
      <div>
        <CommonConfigComponent
          {...props}
          hideConfigPlaceholder
          hideConfigDefaultValue
        />

        <FormItem
          name="defaultValueReference"
          label="Valore di Default"
          help="Configurazione opzionale per precaricare la mappa a partire da un tag semantico di tipo Mappa (es. la mappa compilata in Front Office)."
        >
          <ExpressionReferenceInput
            size="large"
            name="defaultValueReference"
            references={defaultValueReferences}
            width="100%"
          />
        </FormItem>
      </div>

      <WidgetConfigurationSlotRender slot={MapPositionSlot}>
        <FormItem
          name={'position'}
          label="Posizionamento della mappa"
          help="Trascina la mappa per individuare il centro e il livello di zoom desiderato, quindi premi su 'Seleziona centro e zoom' per salvare l'informazione. La mappa verrà presentata all'utente compilatore secondo i critieri indicati."
        >
          <MapExtentInput name="position" />
        </FormItem>
      </WidgetConfigurationSlotRender>

      <WidgetConfigurationSlotRender slot={MapSelectionSlot}>
        <RowElement>
          <FormItem
            name="selection.multiple"
            label="Selezione multipla"
            help="Se abilitato, l'utente potrà selezionare più geometrie contemporaneamente."
          >
            <CheckboxInput name="selection.multiple">
              Abilita selezione multipla
            </CheckboxInput>
          </FormItem>
          <FormItem name={'selection.shapes'} label="Geometrie Selezionabili">
            <CheckboxGroupInput
              name="selection.shapes"
              options={[
                { label: 'Punti', value: MapWidgetSelectionShape.Point },
                { label: 'Linee', value: MapWidgetSelectionShape.Line },
                { label: 'Poligoni', value: MapWidgetSelectionShape.Polygon },
              ]}
            />
          </FormItem>
        </RowElement>

        <RowElement>
          <FormItem
            name="selection.hideGeometriesTable"
            label="Nascondi tabella geometrie"
            help="Se abilitato, la tabella delle geometrie selezionate verrà nascosta."
          >
            <CheckboxInput name="selection.hideGeometriesTable">
              Nascondi tabella geometrie
            </CheckboxInput>
          </FormItem>
          {showBufferDistance && (
            <FormItem
              name="bufferDistance"
              label="Distanza di buffer (m)"
              help="Distanza in metri applicata attorno a punti e linee per trasformarli in poligoni nel calcolo di intersezioni e competenza: raggio per i punti, distanza per lato per le linee."
            >
              <DecimalInput name="bufferDistance" step={0.1} suffix="m" />
            </FormItem>
          )}
        </RowElement>
      </WidgetConfigurationSlotRender>

      <WidgetConfigurationSlotRender slot={MapLabelingSlot}>
        <RowElement>
          <FormItem
            name="labeling.enabled"
            label="Abilita etichette"
            help="Se abilitato, le geometrie disegnate mostreranno un'etichetta numerata (es. # 1, # 2)."
          >
            <CheckboxInput name="labeling.enabled">
              Abilita etichette
            </CheckboxInput>
          </FormItem>
          <FormItem
            name="labeling.prefix"
            label="Prefisso etichetta"
            help="Testo mostrato prima del numero (es. '#' → '# 1')."
          >
            <TextInput name="labeling.prefix" placeholder="#" />
          </FormItem>
        </RowElement>
        <RowElement>
          <FormItem
            name="labeling.zoomRange.min"
            label="Zoom minimo"
            help="Le etichette saranno visibili solo a partire da questo livello di zoom."
          >
            <DecimalInput name="labeling.zoomRange.min" />
          </FormItem>
          <FormItem
            name="labeling.zoomRange.max"
            label="Zoom massimo"
            help="Le etichette saranno nascoste oltre questo livello di zoom."
          >
            <DecimalInput name="labeling.zoomRange.max" />
          </FormItem>
        </RowElement>
      </WidgetConfigurationSlotRender>

      <WidgetConfigurationSlotRender slot={MapGroupsAndLayersSlot}>
        <MapGroupsAndLayersTreeSection
          groupEditor={groupEditor}
          layersEditor={layersEditor}
        />
      </WidgetConfigurationSlotRender>

      {showIntersectionsPanel && (
        <WidgetConfigurationSlotRender slot={MapIntersectionsSlot}>
          <MapIntersectionsConfigSection
            layersEditor={layersEditor}
            intersectionLayers={intersectionLayers}
          />
        </WidgetConfigurationSlotRender>
      )}

      {MapWidgetLogic.isAdvancedMapWidgetEnabled() && (
        <WidgetConfigurationSlotRender slot={MapIdentifySlot}>
          <MapIdentifyConfigSection layersEditor={layersEditor} />
        </WidgetConfigurationSlotRender>
      )}

      <WidgetConfigurationSlotRender slot={MapMunicipalityGroupSlot}>
        <FormItem
          name="municipalityGroupList"
          label="Gruppo Comuni"
          help="Selezionando un gruppo, verranno filtrate le province e i comuni disponibili nei menù a tendina."
        >
          <MunicipalityGroupListSelectInput name="municipalityGroupList" />
        </FormItem>
      </WidgetConfigurationSlotRender>
      <WidgetConfigurationSlotRender slot={MapCompetencesSlot}>
        <MapCompetencesConfigSection layersEditor={layersEditor} />
      </WidgetConfigurationSlotRender>

      <WidgetConfigurationSlotRender slot={MapPrintImagesSlot}>
        <MapPrintImagesConfigSection name="printImages" />
      </WidgetConfigurationSlotRender>
    </>
  );
}
